import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Checkbox,
  CountrySelect,
  OtpInput,
  PasswordField,
  PhoneField,
  TextField,
  countryOptions,
  type CountryOption,
} from '@kcm/ui'
import { AccountCreateRequest, ErrorCode, OtpVerifyRequest } from '@kcm/contracts'
import { useTranslate } from '@tolgee/react'
import { Turnstile } from '../components/Turnstile'
import { PartnerCode } from '../components/PartnerCode'
import { useSite } from '../components/SiteProvider'
import { pathForStep } from '../components/FunnelLayout'
import { ApiError } from '../lib/api'
import { readConsent } from '../lib/consent'
import { endpoints } from '../lib/endpoints'
import { detectCountry } from '../lib/geo'
import { useLocale } from '../hooks/useLocale'
import { asApiError, fieldError } from '../lib/validation'
import { maskEmail, useFunnel } from '../store/funnel'

/**
 * Step 1 — account (PLAN.md §13.1).
 *
 * Three sub-phases behind one step, and the split is the point. The legacy
 * funnel asked for everything at once behind a single Submit, so a person who
 * mistyped their password lost the whole form. Here the email is proven first,
 * which also means the profile form is filled by someone who has demonstrably
 * got the address — findings #2 and #3 in one change.
 */
type Phase = 'email' | 'otp' | 'profile'

export function StepAccount() {
  const [phase, setPhase] = useState<Phase>(() =>
    useFunnel.getState().challengeId ? 'otp' : 'email',
  )
  return phase === 'email' ? (
    <EmailPhase onSent={() => setPhase('otp')} />
  ) : phase === 'otp' ? (
    <OtpPhase onVerified={() => setPhase('profile')} onRestart={() => setPhase('email')} />
  ) : (
    <ProfilePhase />
  )
}

// ── phase 1: the address ─────────────────────────────────────────────────────

function EmailPhase({ onSent }: { onSent: () => void }) {
  const site = useSite()
  const { t } = useTranslate()
  const locale = useLocale()
  const startChallenge = useFunnel((s) => s.startChallenge)
  const [email, setEmail] = useState('')
  const [token, setToken] = useState('')
  // Bumped after every failed attempt: a Turnstile token is single-use, and
  // resubmitting a spent one fails for a reason the user cannot see.
  const [widget, setWidget] = useState(0)
  // The challenge cannot run here at all — most often a site key registered
  // for a different hostname, which is exactly the case on a staging domain.
  const [challengeUnavailable, setChallengeUnavailable] = useState(false)

  const send = useMutation({
    mutationFn: () =>
      endpoints.otp.send({
        email: email.trim().toLowerCase(),
        ...(token ? { turnstileToken: token } : {}),
        locale,
      }),
    onSuccess: (res) => {
      startChallenge(res.challengeId, email.trim().toLowerCase())
      onSent()
    },
    onError: () => {
      setToken('')
      setWidget((n) => n + 1)
    },
  })

  const error = send.error instanceof ApiError ? send.error : null

  // Waiting on a challenge that is still working is worth a disabled button;
  // waiting on one that will never answer is a form nobody can submit. The
  // server holds the real gate (§18.2), so let it be the one to refuse.
  const blocked = site.turnstileSiteKey !== null && !token && !challengeUnavailable

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!send.isPending) send.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">{t('account.emailTitle')}</h1>
        <p className="mt-1 text-sm text-muted">{t('account.emailSubtitle')}</p>
      </div>

      {error && (
        <Alert tone="error" title={titleFor(error, t)}>
          {error.message}
        </Alert>
      )}

      <TextField
        label={t('common.email')}
        type="email"
        name="email"
        inputMode="email"
        autoComplete="email"
        autoFocus
        required
        value={email}
        error={fieldError(error, 'email')}
        onChange={(e) => setEmail(e.target.value)}
      />

      {site.turnstileSiteKey && (
        <Turnstile
          siteKey={site.turnstileSiteKey}
          resetKey={widget}
          locale={locale}
          onToken={(next) => {
            setToken(next)
            if (next) setChallengeUnavailable(false)
          }}
          onUnavailable={() => setChallengeUnavailable(true)}
        />
      )}

      {challengeUnavailable && !error && (
        <Alert tone="info" title={t('account.botFailedTitle')}>
          {t('account.botFailedBody')}
        </Alert>
      )}

      <Button type="submit" disabled={send.isPending || email.trim() === '' || blocked}>
        {send.isPending ? t('account.sending') : t('account.send')}
      </Button>

      <p className="text-center text-sm text-muted">
        {t('account.haveAccount')}{' '}
        <a className="font-medium text-brand underline" href="/login">
          {t('account.login')}
        </a>
      </p>
    </form>
  )
}

// ── phase 2: the code ────────────────────────────────────────────────────────

function OtpPhase({ onVerified, onRestart }: { onVerified: () => void; onRestart: () => void }) {
  const navigate = useNavigate()
  const site = useSite()
  const { t } = useTranslate()
  const locale = useLocale()
  const { challengeId, email, clearChallenge, reset } = useFunnel()
  const [pin, setPin] = useState('')
  const [escalated, setEscalated] = useState(false)

  const verify = useMutation({
    mutationFn: () => endpoints.otp.verify(OtpVerifyRequest.parse({ challengeId, pin })),
    onSuccess: (res) => {
      clearChallenge()
      // A returning user may already be past step 1, in which case the profile
      // form would ask for a lead the CRM already holds.
      if (res.step > 1) navigate(pathForStep(res.step), { replace: true })
      else onVerified()
    },
    onError: () => setPin(''),
  })

  const resend = useMutation({
    mutationFn: (escalate: boolean) =>
      endpoints.otp.resend({
        challengeId: challengeId ?? '',
        escalate,
        locale,
      }),
    onSuccess: (res, escalate) => {
      useFunnel.getState().startChallenge(res.challengeId, email ?? '')
      if (escalate) setEscalated(true)
    },
  })

  const error = (verify.error ?? resend.error) instanceof ApiError
    ? ((verify.error ?? resend.error) as ApiError)
    : null

  // The challenge is gone for good — a new code has to start from the address.
  const dead = error?.code === ErrorCode.OTP_EXPIRED || error?.code === ErrorCode.OTP_LOCKED

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-bold text-brand">{t('account.otpTitle')}</h1>
        <p className="mt-1 text-sm text-muted">
          {t('account.otpSentTo', {
            target: email ? maskEmail(email) : t('account.otpTargetFallback'),
          })}
        </p>
      </div>

      {error && (
        <Alert tone="error" title={titleFor(error, t)}>
          {error.message}
          {/* The server puts the remaining attempts here so the warning arrives
              before the lockout rather than with it. */}
          {fieldError(error, 'pin') && <span className="block">{fieldError(error, 'pin')}</span>}
        </Alert>
      )}

      {escalated && !error && (
        <Alert tone="info">{t('account.escalated')}</Alert>
      )}

      {dead ? (
        <Button
          onClick={() => {
            reset()
            onRestart()
          }}
        >
          {t('account.startAgain')}
        </Button>
      ) : (
        <>
          <OtpInput
            value={pin}
            onChange={(next) => {
              setPin(next)
              // Six digits is unambiguous; making them press a button as well is
              // friction with no purpose (§11.5).
              if (next.length === 6 && !verify.isPending) verify.mutate()
            }}
          />

          <Button
            type="button"
            disabled={verify.isPending || pin.length !== 6}
            onClick={() => verify.mutate()}
          >
            {verify.isPending ? t('account.checking') : t('account.verify')}
          </Button>

          <div className="flex flex-col gap-2 text-sm">
            <button
              type="button"
              className="text-brand underline disabled:text-muted"
              disabled={resend.isPending}
              onClick={() => resend.mutate(false)}
            >
              {t('account.resend')}
            </button>
            {/* Escalation, not a retry: the provider that already reported
                success will keep reporting success (§8). */}
            <button
              type="button"
              className="text-brand underline disabled:text-muted"
              disabled={resend.isPending || escalated}
              onClick={() => resend.mutate(true)}
            >
              {t('account.tryAnotherWay')}
            </button>
            <button
              type="button"
              className="text-muted underline"
              onClick={() => {
                reset()
                onRestart()
              }}
            >
              {t('account.differentEmail')}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ── phase 3: the lead ────────────────────────────────────────────────────────

function ProfilePhase() {
  const navigate = useNavigate()
  const site = useSite()
  const { t } = useTranslate()
  const locale = useLocale()
  const email = useFunnel((s) => s.email)
  const [form, setForm] = useState({
    country: '',
    phone: '',
    password: '',
  })
  const [agreed, setAgreed] = useState(false)
  const set = (key: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }))

  const countries = useQuery({
    queryKey: ['countries'],
    queryFn: ({ signal }) => endpoints.countries(signal),
    staleTime: 60 * 60 * 1000,
  })

  const geo = useQuery({
    queryKey: ['geo-country'],
    // Never `undefined`: TanStack treats that as a query that returned nothing
    // and retries. `''` is a real answer meaning "we could not tell".
    queryFn: async () => (await detectCountry()) ?? '',
    staleTime: Infinity,
    retry: false,
  })

  /**
   * The CRM's list when it is there, the bundled one when it is not.
   *
   * `/meta/countries` is a call to a third party on the first field of the
   * funnel, and it fails closed: a 503 from the CRM used to leave an empty
   * picker with no country selectable, which is the entire funnel down for
   * everyone. The bundled list keeps step 1 answerable. It is only names and
   * dial codes — eligibility is still decided by POST /account, which checks
   * `isRestricted` server-side whatever the picker offered.
   *
   * Dial codes come from the bundle in both cases; the CRM dictionary is names
   * and codes only, and PhoneField needs a prefix for the country either way.
   */
  const options = useMemo<CountryOption[]>(() => {
    const bundled = countryOptions(locale)
    const fromCrm = countries.data?.countries
    if (!fromCrm?.length) return bundled

    const dialCodes = new Map(bundled.map((c) => [c.code, c.dialCode]))
    return fromCrm.map((c) => ({ ...c, dialCode: dialCodes.get(c.code) ?? '' }))
  }, [countries.data, locale])

  /**
   * Where the visitor's IP says they are, offered as a starting point.
   *
   * Fills the country of residence, and through it the phone prefix — the field
   * below follows this one while the number is still empty. Both stay editable;
   * an IP is a guess and a VPN or a roaming SIM makes it a wrong one.
   *
   * Two conditions guard it. `touched` retires the guess the moment the user has
   * an opinion, including the "opinion" of clearing the field, so it can never
   * put back something they removed. And the country has to be one the picker is
   * offering — prefilling a jurisdiction the CRM does not accept would walk them
   * into a rejection on submit that they did not choose and cannot read.
   */
  const [touched, setTouched] = useState(false)
  const guess = geo.data
  useEffect(() => {
    if (touched || !guess) return
    if (!options.some((o) => o.code === guess)) return
    setForm((f) => (f.country === '' ? { ...f, country: guess } : f))
  }, [guess, options, touched])

  const create = useMutation({
    mutationFn: async () => {
      const consent = readConsent()
      const parsed = AccountCreateRequest.safeParse({
        country: form.country,
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        password: form.password,
        locale,
        ...(consent
          ? {
              consent: {
                version: consent.version,
                analytics: consent.analytics,
                marketing: consent.marketing,
              },
            }
          : {}),
      })
      // Parsing here saves a round trip on a weak password, but a raw ZodError
      // is not an ApiError and the screen would render nothing at all — the
      // button would simply appear to do nothing.
      if (!parsed.success) throw asApiError(parsed.error)
      return endpoints.account(parsed.data)
    },
    onSuccess: (res) => navigate(pathForStep(res.step), { replace: true }),
  })

  const error = create.error instanceof ApiError ? create.error : null

  // Nothing on this form can fix an account that already exists. Sending them
  // to sign in beats leaving them to guess (finding #17 permits saying so: the
  // OTP already proved they own the address).
  if (error?.code === ErrorCode.ACCOUNT_EXISTS) {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="info" title={t('account.existsTitle')}>
          {error.message}
        </Alert>
        <Button onClick={() => window.location.assign('/login')}>
          {t('account.signInInstead')}
        </Button>
      </div>
    )
  }

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!create.isPending) create.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">{t('account.profileTitle')}</h1>
        <p className="mt-1 text-sm text-muted">{t('account.profileSubtitle')}</p>
      </div>

      {error && (
        <Alert tone="error" title={titleFor(error, t)}>
          {error.message}
        </Alert>
      )}

      {/*
        The address the code was sent to, and the reason a password manager can
        save anything here at all: they store a pair, and a form with a new
        password and no username in it is one they either skip or file under a
        guess. `readOnly` rather than hidden — a manager ignores what it cannot
        see, and it is worth showing anyway, since this is the last chance to
        notice the code went to the wrong address.
      */}
      {email && (
        <TextField
          label={t('common.email')}
          type="email"
          name="email"
          autoComplete="username"
          readOnly
          value={email}
          hint={t('account.emailReadonlyHint')}
          className="bg-surface-alt text-muted"
        />
      )}

      <CountrySelect
        label={t('account.country')}
        required
        placeholder={t('account.countrySearch')}
        value={form.country}
        options={options}
        error={fieldError(error, 'country')}
        onChange={(code) => {
          // Any change at all, including clearing it, means the IP guess has
          // been overruled and must not come back.
          setTouched(true)
          set('country')(code)
        }}
      />

      <PhoneField
        label={t('account.phone')}
        name="tel-national"
        value={form.phone}
        onChange={set('phone')}
        // Opens on the country of residence, and only while the field is still
        // empty — most people's number belongs to where they live, and the ones
        // for whom it does not have already typed by then. That is also how the
        // prefix follows the country guessed from the IP.
        country={form.country}
        locale={locale}
        error={fieldError(error, 'phone')}
      />

      <PasswordField
        label={t('common.password')}
        required
        name="new-password"
        autoComplete="new-password"
        value={form.password}
        error={fieldError(error, 'password')}
        onChange={(e) => set('password')(e.target.value)}
      />

      <PartnerCode />

      <Checkbox
        label={
          <span>
            {t('account.consentBefore')}{' '}
            <a className="underline" href={site.legal.privacyUrl} target="_blank" rel="noreferrer">
              {t('shell.privacyPolicy')}
            </a>
            {t('account.consentAfter')}
          </span>
        }
        checked={agreed}
        onChange={(e) => setAgreed(e.target.checked)}
      />

      <Button type="submit" disabled={create.isPending || !agreed || form.country === ''}>
        {create.isPending ? t('account.creating') : t('common.continue')}
      </Button>
    </form>
  )
}

// ── shared ───────────────────────────────────────────────────────────────────

/**
 * A short heading per error class. The body is always the server's message —
 * it is already display copy and already localised.
 */
function titleFor(error: ApiError, t: (key: string) => string): string {
  switch (error.code) {
    case ErrorCode.RATE_LIMITED:
      return t('err.tooManyAttempts')
    case ErrorCode.TURNSTILE_FAILED:
      return t('err.checkFailed')
    case ErrorCode.OTP_DELIVERY_FAILED:
      return t('err.otpDeliveryFailed')
    case ErrorCode.OTP_LOCKED:
      return t('err.otpLocked')
    case ErrorCode.OTP_EXPIRED:
      return t('err.otpExpired')
    case ErrorCode.COUNTRY_NOT_PERMITTED:
      return t('err.countryNotPermitted')
    case ErrorCode.CRM_UNAVAILABLE:
      return t('err.temporarilyUnavailable')
    default:
      return t('err.somethingWrong')
  }
}
